import { useEffect, useMemo, useState, type FormEvent } from "react";
import type { ApiClient } from "../api/client.js";
import { ApiError, HttpApiClient } from "../api/client.js";
import type {
  Contact,
  DuplicateCandidate,
  Lab,
  LocationDetail,
  Location,
  LabDetail,
  Me,
  Organization,
  OrganizationLocation,
  QueueItem,
  SavedInteraction,
  SearchItem,
  ServiceProfileSummary,
} from "../api/types.js";
import {
  AuthConfigurationError,
  AuthProviderError,
  createEnvironmentAuthClient,
  type AuthClient,
} from "../auth/auth.js";
import {
  categoryLabel,
  formatDate,
  formatLocalDate,
  formatDerivedWindow,
  formatObservedTiming,
  formatProvider,
  formatQuantity,
  gapLabel,
  matchFieldLabel,
  missingFacts,
  needsAttentionMark,
  queueLabel,
  queueReason,
  queueReasonShort,
  serviceFacts,
  verificationLabel,
} from "./presentation.js";
import { QuickInteractionCapture } from "../features/interactions/QuickInteractionCapture.js";
import { ObservationCorrectionForm } from "../features/observations/ObservationCorrectionForm.js";
import { WorkbenchTable } from "../features/workbench/WorkbenchTable.js";
import {
  hasActiveFilters,
  lensLabel,
  matchesLocalSearch,
  parseWorkbenchState,
  serializeWorkbenchState,
  toWorkbenchQuery,
  type WorkbenchState,
} from "./workbench-state.js";

type Route =
  | { kind: "today"; workbench: WorkbenchState }
  | { kind: "territory" }
  | { kind: "search"; query?: string }
  | { kind: "organization"; id: string }
  | { kind: "location"; id: string }
  | { kind: "lab"; id: string };

/**
 * Remembers a working set so detail navigation is not destructive. Exported so
 * tests can isolate cases; nothing in the product mutates it directly.
 */
export interface CachedWorkbench {
  items: QueueItem[];
  nextCursor: string | null;
  scroll: number;
  /** Set after a save so the set is refetched instead of trusted. */
  stale?: boolean;
}

export const workbenchCache = new Map<string, CachedWorkbench>();

function workbenchCacheKey(state: WorkbenchState): string {
  return serializeWorkbenchState({ ...state, selectedProfileId: undefined });
}

function parseRoute(pathname: string, search: string): Route {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "search") {
    const query = new URLSearchParams(search).get("q");
    return { kind: "search", ...(query ? { query } : {}) };
  }
  if (segments[0] === "territory") return { kind: "territory" };
  if (segments[0] === "organizations" && segments[1])
    return { kind: "organization", id: segments[1] };
  if (segments[0] === "locations" && segments[1])
    return { kind: "location", id: segments[1] };
  if (segments[0] === "labs" && segments[1])
    return { kind: "lab", id: segments[1] };
  return { kind: "today", workbench: parseWorkbenchState(search) };
}

function currentSearch(): string {
  return window.location.search;
}

function routeFromPath(path: string): Route {
  const target = new URL(path, window.location.origin);
  return parseRoute(target.pathname, target.search);
}

interface AppProps {
  api?: ApiClient;
  authClient?: AuthClient;
  initialPath?: string;
}

function todayPath(): string {
  return window.location.pathname || "/";
}

/** The workbench is served at `/` and at `/today`; keep whichever is in use. */
function workbenchPath(): string {
  const path = todayPath();
  return path === "/today" ? "/today" : "/";
}

function ErrorPanel({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state-panel error-panel" role="alert">
      <strong>We couldn’t load that CRM view.</strong>
      <p>{message}</p>
      {onRetry && (
        <button type="button" className="secondary-button" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

function LoadingPanel({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="state-panel" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" /> {label}
    </div>
  );
}

type IconName = "search" | "plus" | "arrow-right" | "back" | "close";

function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (name === "search")
    return (
      <svg {...common}>
        <title>{name}</title>
        <circle cx="11" cy="11" r="6.5" />
        <path d="m16 16 4 4" />
      </svg>
    );
  if (name === "plus")
    return (
      <svg {...common}>
        <title>{name}</title>
        <path d="M12 5v14M5 12h14" />
      </svg>
    );
  if (name === "arrow-right")
    return (
      <svg {...common}>
        <title>{name}</title>
        <path d="M5 12h13M13 6l6 6-6 6" />
      </svg>
    );
  if (name === "back")
    return (
      <svg {...common}>
        <title>{name}</title>
        <path d="M19 12H5M11 6l-6 6 6 6" />
      </svg>
    );
  return (
    <svg {...common}>
      <title>{name}</title>
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  );
}

export function App({
  api: injectedApi,
  authClient: injectedAuth,
  initialPath,
}: AppProps) {
  const authClient = useMemo(
    () => injectedAuth ?? createEnvironmentAuthClient(),
    [injectedAuth],
  );
  const api = useMemo(
    () =>
      injectedApi ??
      new HttpApiClient(import.meta.env.VITE_API_BASE_URL ?? "", () =>
        authClient.getAccessToken(),
      ),
    [authClient, injectedApi],
  );
  const [authState, setAuthState] = useState<
    "loading" | "authenticated" | "unauthenticated" | "inactive" | "unavailable"
  >("loading");
  const [actor, setActor] = useState<Me | null>(null);
  const [authMessage, setAuthMessage] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [globalCaptureOpen, setGlobalCaptureOpen] = useState(false);
  const [globalNotice, setGlobalNotice] = useState<string | null>(null);
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  const [route, setRoute] = useState<Route>(() =>
    routeFromPath(initialPath ?? `${todayPath()}${currentSearch()}`),
  );
  /**
   * A Lab opened from a working set returns to that set and its filters; a Lab
   * reached through Territory still falls back to Territory. The workbench is
   * served at `/` and at `/today`, so preserve whichever one was in use.
   */
  const workbenchFallback = `${workbenchPath()}${currentSearch()}`;

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const token = await authClient.getAccessToken();
        if (!token) {
          if (active) {
            setAuthState("unauthenticated");
            setAuthMessage(
              "Sign in through the approved Exact Pro identity provider to use the CRM.",
            );
          }
          return;
        }
        const me = await api.getMe();
        if (active) {
          setActor(me);
          setAuthState("authenticated");
        }
      } catch (caught) {
        if (!active) return;
        if (caught instanceof AuthConfigurationError) {
          setAuthState("unavailable");
          setAuthMessage(caught.message);
        } else if (
          caught instanceof ApiError &&
          caught.code === "inactive_user"
        ) {
          setAuthState("inactive");
          setAuthMessage(
            "This CRM account is inactive. Contact an Exact Pro administrator.",
          );
        } else if (
          caught instanceof ApiError &&
          (caught.code === "unauthenticated" || caught.status === 401)
        ) {
          setAuthState("unauthenticated");
          setAuthMessage(
            "Your CRM session is not authenticated. Sign in again to continue.",
          );
        } else {
          setAuthState("unavailable");
          setAuthMessage(
            caught instanceof Error
              ? caught.message
              : "The CRM API is unavailable.",
          );
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [api, authClient]);

  useEffect(() => {
    const handlePopState = () =>
      setRoute(parseRoute(window.location.pathname, window.location.search));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  function navigate(path: string) {
    setPreviousPath(
      `${window.location.pathname}${window.location.search}` || "/",
    );
    window.history.pushState({}, "", path);
    // Working-set state travels in the query string, so it must be parsed back
    // out; dropping it would silently reset the user's filters and lens.
    setRoute(routeFromPath(path));
  }

  function goBack(fallback: string) {
    const path = previousPath ?? fallback;
    setPreviousPath(null);
    window.history.pushState({}, "", path);
    setRoute(routeFromPath(path));
  }

  async function handleSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!authClient.signInWithPassword) {
      setAuthState("unavailable");
      setAuthMessage("Email/password sign-in is not available.");
      return;
    }

    setIsSigningIn(true);
    setAuthMessage("");
    try {
      await authClient.signInWithPassword(authEmail.trim(), authPassword);
      const me = await api.getMe();
      setActor(me);
      setAuthPassword("");
      setAuthState("authenticated");
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "inactive_user") {
        setAuthState("inactive");
        setAuthMessage(
          "This CRM account is inactive. Contact an Exact Pro administrator.",
        );
      } else if (caught instanceof AuthProviderError) {
        setAuthState("unauthenticated");
        setAuthMessage("Sign-in failed. Check your email and password.");
      } else if (caught instanceof ApiError && caught.status === 401) {
        setAuthState("unauthenticated");
        setAuthMessage("Your CRM account is not authorized to use this CRM.");
      } else {
        setAuthState("unavailable");
        setAuthMessage(
          caught instanceof Error
            ? caught.message
            : "The CRM API is unavailable.",
        );
      }
    } finally {
      setIsSigningIn(false);
    }
  }

  if (authState === "loading")
    return <LoadingPanel label="Starting Exact Pro CRM…" />;
  if (authState !== "authenticated" || !actor) {
    return (
      <main className="auth-state">
        <div className="brand-mark">EP</div>
        <p className="eyebrow">Exact Pro CRM</p>
        <h1>Territory knowledge that stays useful.</h1>
        <p>{authMessage}</p>
        {authState === "unauthenticated" && authClient.signInWithPassword && (
          <form
            className="auth-form"
            aria-label="Sign in"
            onSubmit={handleSignIn}
          >
            <label>
              Email
              <input
                type="email"
                value={authEmail}
                onChange={(event) => setAuthEmail(event.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={authPassword}
                onChange={(event) => setAuthPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button
              type="submit"
              className="primary-button"
              disabled={isSigningIn}
            >
              {isSigningIn ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}
        {authState === "unavailable" && (
          <button
            type="button"
            className="secondary-button"
            onClick={() => window.location.reload()}
          >
            Retry connection
          </button>
        )}
      </main>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <button
          className="wordmark"
          type="button"
          onClick={() => navigate("/")}
          aria-label="Go to Today"
        >
          <span className="brand-mark small">EP</span>
          <span>
            Exact Pro <b>CRM</b>
          </span>
        </button>
        <nav className="primary-nav" aria-label="Primary navigation">
          <button
            type="button"
            className={route.kind === "today" ? "nav-link active" : "nav-link"}
            onClick={() => navigate("/")}
          >
            Today
          </button>
          <button
            type="button"
            className={
              route.kind === "territory" || route.kind === "organization"
                ? "nav-link active"
                : "nav-link"
            }
            onClick={() => navigate("/territory")}
          >
            Territory
          </button>
        </nav>
        <GlobalSearch api={api} onNavigate={navigate} />
        {actor.capabilities.includes("interaction:write") && (
          <button
            type="button"
            className="primary-button header-action"
            onClick={() => {
              setGlobalNotice(null);
              setGlobalCaptureOpen(true);
            }}
          >
            <Icon name="plus" size={16} /> Record interaction
          </button>
        )}
        <div className="actor-chip" title={actor.email}>
          <span className="status-dot" />
          {actor.displayName}
        </div>
      </header>
      <main className="app-main">
        {route.kind === "today" && (
          <TodayView
            api={api}
            state={route.workbench}
            onNavigate={navigate}
            canWriteInteractions={actor.capabilities.includes(
              "interaction:write",
            )}
          />
        )}
        {route.kind === "search" && (
          <SearchView
            api={api}
            onNavigate={navigate}
            initialQuery={route.query}
          />
        )}
        {route.kind === "territory" && (
          <TerritoryView
            api={api}
            onNavigate={navigate}
            canWriteTerritory={actor.capabilities.includes("territory:write")}
          />
        )}
        {route.kind === "organization" && (
          <OrganizationView
            api={api}
            organizationId={route.id}
            onNavigate={navigate}
            onBack={() => goBack("/territory")}
            canWriteTerritory={actor.capabilities.includes("territory:write")}
          />
        )}
        {route.kind === "location" && (
          <LocationView
            api={api}
            locationId={route.id}
            onNavigate={navigate}
            onBack={() => goBack("/territory")}
            canWriteTerritory={actor.capabilities.includes("territory:write")}
            canWriteInteractions={actor.capabilities.includes(
              "interaction:write",
            )}
          />
        )}
        {route.kind === "lab" && (
          <LabView
            api={api}
            labId={route.id}
            onBack={() => goBack(workbenchFallback)}
            onOpenTerritory={() => navigate("/territory")}
            fromWorkbench={
              previousPath !== null && previousPath !== "/territory"
            }
            canWriteInteractions={actor.capabilities.includes(
              "interaction:write",
            )}
            canCorrectObservations={actor.capabilities.includes(
              "observation:correct",
            )}
          />
        )}
      </main>
      {globalNotice && (
        <div className="success-message global-notice" role="status">
          {globalNotice}
        </div>
      )}
      {globalCaptureOpen &&
        actor.capabilities.includes("interaction:write") && (
          <GlobalInteractionLauncher
            api={api}
            onClose={() => setGlobalCaptureOpen(false)}
            onSaved={async (_result, summary) => {
              setGlobalCaptureOpen(false);
              setGlobalNotice(summary);
            }}
          />
        )}
    </div>
  );
}

function GlobalSearch({
  onNavigate,
}: {
  api: ApiClient;
  onNavigate: (path: string) => void;
}) {
  const [query, setQuery] = useState("");

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = query.trim();
    if (value) onNavigate(`/search?q=${encodeURIComponent(value)}`);
  }

  return (
    <form className="header-search" onSubmit={submit}>
      <label className="sr-only" htmlFor="global-search">
        Search labs, people, locations, and organizations
      </label>
      <span className="search-icon" aria-hidden="true">
        <Icon name="search" />
      </span>
      <input
        id="global-search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search labs, people, locations..."
      />
      <button type="submit" className="search-submit">
        Search
      </button>
    </form>
  );
}

function TerritoryView({
  api,
  onNavigate,
  canWriteTerritory,
}: {
  api: ApiClient;
  onNavigate: (path: string) => void;
  canWriteTerritory: boolean;
}) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newOrganizationOpen, setNewOrganizationOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .getOrganizations(50)
      .then((page) => {
        if (!active) return;
        setOrganizations([...page.items]);
        setNextCursor(page.nextCursor);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "Territory is unavailable.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, refresh]);

  const filteredOrganizations = organizations.filter((item) =>
    item.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await api.getOrganizations(50, nextCursor);
      setOrganizations((current) => [...current, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Territory is unavailable.",
      );
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section className="page-section">
      <div className="compact-page-heading">
        <div>
          <p className="eyebrow">Territory</p>
          <h1>Work the territory</h1>
          <p className="lede">Browse places and open the context you need.</p>
        </div>
        {canWriteTerritory && (
          <button
            type="button"
            className="primary-button"
            onClick={() => setNewOrganizationOpen(true)}
          >
            <Icon name="plus" size={16} /> New organization
          </button>
        )}
      </div>
      <label className="search-field compact-search-field">
        <span className="sr-only">Filter organizations</span>
        <span className="search-icon" aria-hidden="true">
          <Icon name="search" />
        </span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter organizations..."
        />
      </label>
      {loading && <LoadingPanel label="Loading territory..." />}
      {error && <ErrorPanel message={error} />}
      {!loading && !error && filteredOrganizations.length === 0 && (
        <div className="empty-state">
          <strong>
            {query ? "No matching organizations." : "No organizations yet."}
          </strong>
          <p>
            Territory browsing starts with an organization, then a campus or
            building.
          </p>
          {canWriteTerritory && !query && (
            <button
              type="button"
              className="primary-button"
              onClick={() => setNewOrganizationOpen(true)}
            >
              Add organization
            </button>
          )}
        </div>
      )}
      {!loading && !error && filteredOrganizations.length > 0 && (
        <div className="territory-list">
          {filteredOrganizations.map((organization) => (
            <button
              type="button"
              className="organization-row"
              key={organization.id}
              onClick={() => onNavigate(`/organizations/${organization.id}`)}
            >
              <span>
                <b>{organization.name}</b>
                <small>
                  {organization.organizationType?.replaceAll("_", " ") ||
                    "Organization"}
                </small>
              </span>
              <span className="row-arrow" aria-hidden="true">
                <Icon name="arrow-right" size={17} />
              </span>
            </button>
          ))}
        </div>
      )}
      {!loading && !error && nextCursor && !query && (
        <button
          type="button"
          className="secondary-button load-more-button"
          onClick={() => void loadMore()}
          disabled={loadingMore}
        >
          {loadingMore ? "Loading..." : "Load more organizations"}
        </button>
      )}
      {newOrganizationOpen && canWriteTerritory && (
        <NewOrganizationForm
          api={api}
          onClose={() => setNewOrganizationOpen(false)}
          onCreated={(organization) => {
            setNewOrganizationOpen(false);
            setRefresh((value) => value + 1);
            onNavigate(`/organizations/${organization.id}`);
          }}
        />
      )}
    </section>
  );
}

function OrganizationView({
  api,
  organizationId,
  onNavigate,
  onBack,
  canWriteTerritory,
}: {
  api: ApiClient;
  organizationId: string;
  onNavigate: (path: string) => void;
  onBack: () => void;
  canWriteTerritory: boolean;
}) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [locations, setLocations] = useState<OrganizationLocation[]>([]);
  const [nextLocationCursor, setNextLocationCursor] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newLocationOpen, setNewLocationOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void Promise.all([
      api.getOrganizations(100),
      api.getOrganizationLocations(organizationId, 50),
    ])
      .then(([organizationPage, locationPage]) => {
        if (!active) return;
        setOrganization(
          organizationPage.items.find((item) => item.id === organizationId) ??
            null,
        );
        setLocations([...locationPage.items]);
        setNextLocationCursor(locationPage.nextCursor);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "Organization context is unavailable.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, organizationId, refresh]);

  if (loading) return <LoadingPanel label="Loading organization context..." />;
  if (error || !organization)
    return <ErrorPanel message={error ?? "Organization not found."} />;

  async function loadMoreLocations() {
    if (!nextLocationCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await api.getOrganizationLocations(
        organizationId,
        50,
        nextLocationCursor,
      );
      setLocations((current) => [...current, ...page.items]);
      setNextLocationCursor(page.nextCursor);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Organization locations are unavailable.",
      );
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section className="page-section">
      <BackLink onClick={onBack} label="Back to territory" />
      <div className="compact-page-heading">
        <div>
          <p className="eyebrow">Territory / Organization</p>
          <h1>{organization.name}</h1>
          <p className="breadcrumb">
            {organization.organizationType?.replaceAll("_", " ") ||
              "Organization"}
          </p>
        </div>
        <div className="page-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() =>
              onNavigate(
                `${workbenchPath()}${serializeWorkbenchState({
                  lens: "NOW",
                  grouping: "place",
                  organizationId,
                })}`,
              )
            }
          >
            Work this organization
          </button>
          {canWriteTerritory && (
            <button
              type="button"
              className="primary-button"
              onClick={() => setNewLocationOpen(true)}
            >
              <span aria-hidden="true">+</span> New location
            </button>
          )}
        </div>
      </div>
      <section className="content-card">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Places</p>
            <h2>Campuses and buildings</h2>
          </div>
        </div>
        {locations.length === 0 ? (
          <div className="empty-state nested-empty-state">
            <strong>No locations recorded yet.</strong>
            <p>
              Add a campus, building, or site to start working this
              organization.
            </p>
            {canWriteTerritory && (
              <button
                type="button"
                className="primary-button"
                onClick={() => setNewLocationOpen(true)}
              >
                Add location
              </button>
            )}
          </div>
        ) : (
          <div className="territory-list location-tree">
            {locations.map((location) => (
              <button
                type="button"
                className="organization-row location-row"
                key={location.id}
                onClick={() => onNavigate(`/locations/${location.id}`)}
              >
                <span>
                  <b>{location.name}</b>
                  <small>
                    {[
                      ...location.parentLocationBreadcrumb.map(
                        (item) => item.name,
                      ),
                      location.locationType,
                    ]
                      .filter(Boolean)
                      .join(" / ")}
                  </small>
                </span>
                <span className="row-arrow" aria-hidden="true">
                  <Icon name="arrow-right" size={17} />
                </span>
              </button>
            ))}
          </div>
        )}
        {nextLocationCursor && (
          <button
            type="button"
            className="secondary-button load-more-button"
            onClick={() => void loadMoreLocations()}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading..." : "Load more locations"}
          </button>
        )}
      </section>
      {newLocationOpen && canWriteTerritory && (
        <NewLocationForm
          api={api}
          organization={organization}
          locations={locations}
          onClose={() => setNewLocationOpen(false)}
          onCreated={() => {
            setNewLocationOpen(false);
            setRefresh((value) => value + 1);
          }}
        />
      )}
    </section>
  );
}

function NewOrganizationForm({
  api,
  onClose,
  onCreated,
}: {
  api: ApiClient;
  onClose: () => void;
  onCreated: (organization: Organization) => void;
}) {
  const [name, setName] = useState("");
  const [organizationType, setOrganizationType] = useState("");
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(createAnyway = false) {
    setSaving(true);
    setError(null);
    try {
      const organization = await api.createOrganization({
        name: name.trim(),
        organizationType: organizationType || null,
        createAnyway,
      });
      onCreated(organization);
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.code === "duplicate_review_required"
      ) {
        setCandidates(caught.details?.candidates ?? []);
      } else {
        setError(
          caught instanceof Error
            ? caught.message
            : "Organization could not be created.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form
        className="capture-sheet context-sheet"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="sheet-heading">
          <div>
            <p className="eyebrow">New territory</p>
            <h2>Add organization</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <label>
          Organization name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            maxLength={200}
          />
        </label>
        <label>
          Type <span className="muted">(optional)</span>
          <select
            value={organizationType}
            onChange={(event) => setOrganizationType(event.target.value)}
          >
            <option value="">Not recorded</option>
            <option value="university">University</option>
            <option value="biotech">Biotech</option>
            <option value="research_institute">Research institute</option>
            <option value="incubator">Incubator</option>
            <option value="other">Other</option>
          </select>
        </label>
        {candidates.length > 0 && (
          <DuplicateReview
            candidates={candidates}
            title="Possible matching organization"
            onCreateAnyway={() => void submit(true)}
          />
        )}
        {error && <p className="error-message">{error}</p>}
        <div className="sheet-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={saving}>
            {saving ? "Saving..." : "Create organization"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NewLocationForm({
  api,
  organization,
  locations,
  onClose,
  onCreated,
}: {
  api: ApiClient;
  organization: Organization;
  locations: OrganizationLocation[];
  onClose: () => void;
  onCreated: (location: Location) => void;
}) {
  const [name, setName] = useState("");
  const [locationType, setLocationType] = useState("building");
  const [parentLocationId, setParentLocationId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const location = await api.createLocation({
        organizationId: organization.id,
        parentLocationId: parentLocationId || null,
        name: name.trim(),
        locationType,
      });
      onCreated(location);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Location could not be created.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form
        className="capture-sheet context-sheet"
        onSubmit={(event) => void submit(event)}
      >
        <div className="sheet-heading">
          <div>
            <p className="eyebrow">{organization.name}</p>
            <h2>Add location</h2>
            <p className="context-line">
              This location will be added to the current organization.
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="form-grid">
          <label>
            Name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={200}
            />
          </label>
          <label>
            Type
            <select
              value={locationType}
              onChange={(event) => setLocationType(event.target.value)}
            >
              <option value="campus">Campus</option>
              <option value="building">Building</option>
              <option value="site">Site</option>
              <option value="other">Other</option>
            </select>
          </label>
        </div>
        <label>
          Parent location <span className="muted">(optional)</span>
          <select
            value={parentLocationId}
            onChange={(event) => setParentLocationId(event.target.value)}
          >
            <option value="">No parent</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {[
                  ...location.parentLocationBreadcrumb.map((item) => item.name),
                  location.name,
                ].join(" / ")}
              </option>
            ))}
          </select>
        </label>
        {error && <p className="error-message">{error}</p>}
        <div className="sheet-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={saving}>
            {saving ? "Saving..." : "Create location"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NewLabForm({
  api,
  location,
  onClose,
  onCreated,
  onUseExisting,
}: {
  api: ApiClient;
  location: Location;
  onClose: () => void;
  onCreated: (lab: Lab) => void;
  onUseExisting: (labId: string) => void;
}) {
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [roomOrSuite, setRoomOrSuite] = useState("");
  const [notes, setNotes] = useState("");
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(createAnyway = false) {
    setSaving(true);
    setError(null);
    try {
      const lab = await api.createLab({
        primaryLocationId: location.id,
        name: name.trim(),
        ...(department.trim() ? { department: department.trim() } : {}),
        ...(roomOrSuite.trim() ? { roomOrSuite: roomOrSuite.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        createAnyway,
      });
      onCreated(lab);
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.code === "duplicate_review_required"
      ) {
        setCandidates(caught.details?.candidates ?? []);
      } else {
        setError(
          caught instanceof Error
            ? caught.message
            : "Lab could not be created.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void save();
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form className="capture-sheet context-sheet" onSubmit={submit}>
        <div className="sheet-heading">
          <div>
            <p className="eyebrow">{location.name}</p>
            <h2>Add lab</h2>
            <p className="context-line">
              The organization and location are already set.
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="form-grid">
          <label>
            Lab name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={200}
            />
          </label>
          <label>
            Room or suite <span className="muted">(optional)</span>
            <input
              value={roomOrSuite}
              onChange={(event) => setRoomOrSuite(event.target.value)}
              maxLength={100}
            />
          </label>
        </div>
        <label>
          Department <span className="muted">(optional)</span>
          <input
            value={department}
            onChange={(event) => setDepartment(event.target.value)}
            maxLength={200}
          />
        </label>
        <label>
          Note <span className="muted">(optional)</span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={2}
            maxLength={2000}
          />
        </label>
        {candidates.length > 0 && (
          <DuplicateReview
            candidates={candidates}
            title="Possible matching lab"
            onUseExisting={onUseExisting}
            onCreateAnyway={() => void save(true)}
          />
        )}
        {error && <p className="error-message">{error}</p>}
        <div className="sheet-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={saving}>
            {saving ? "Saving..." : "Create lab"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DuplicateReview({
  candidates,
  title,
  onUseExisting,
  onCreateAnyway,
}: {
  candidates: DuplicateCandidate[];
  title: string;
  onUseExisting?: (id: string) => void;
  onCreateAnyway: () => void;
}) {
  return (
    <div className="duplicate-review">
      <strong>{title}</strong>
      <p>Check the existing context before creating another record.</p>
      {candidates.map((candidate) => (
        <div className="candidate" key={candidate.id}>
          <b>{candidate.name}</b>
          <span>
            {[
              candidate.organizationName,
              candidate.locationName,
              candidate.title,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {onUseExisting && (
            <button
              type="button"
              className="text-button"
              onClick={() => onUseExisting(candidate.id)}
            >
              Use existing
            </button>
          )}
        </div>
      ))}
      <div className="button-row">
        <button
          type="button"
          className="secondary-button"
          onClick={onCreateAnyway}
        >
          Create anyway
        </button>
      </div>
    </div>
  );
}

function GlobalInteractionLauncher({
  api,
  onClose,
  onSaved,
}: {
  api: ApiClient;
  onClose: () => void;
  onSaved: (result: SavedInteraction, summary: string) => Promise<void> | void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [selected, setSelected] = useState<{
    lab?: LabDetail;
    location?: LocationDetail;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      void api
        .search(query.trim())
        .then((page) => {
          if (active) setResults([...page.items]);
        })
        .catch((caught: unknown) => {
          if (active)
            setError(
              caught instanceof Error
                ? caught.message
                : "Search is unavailable.",
            );
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, query]);

  async function choose(item: SearchItem) {
    if (item.type !== "lab" && item.type !== "location") return;
    setLoading(true);
    setError(null);
    try {
      if (item.type === "lab")
        setSelected({ lab: await api.getLab(item.lab.id) });
      else setSelected({ location: await api.getLocation(item.location.id) });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "That context is unavailable.",
      );
    } finally {
      setLoading(false);
    }
  }

  const lab = selected?.lab;
  const location = selected?.location;
  if (lab) {
    return (
      <QuickInteractionCapture
        api={api}
        labId={lab.lab.id}
        labName={lab.lab.name}
        location={lab.primaryLocation}
        contacts={lab.contacts.map((relationship) => relationship.contact)}
        relationships={lab.contacts}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  }
  if (location) {
    return (
      <QuickInteractionCapture
        api={api}
        location={location.location}
        contacts={location.contacts.map((relationship) => relationship.contact)}
        availableLabs={location.labs.items.map((entry) => entry.lab)}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  }
  return (
    <div className="modal-backdrop" role="presentation">
      <div className="capture-sheet context-sheet">
        {!selected && (
          <>
            <div className="sheet-heading">
              <div>
                <p className="eyebrow">Quick capture</p>
                <h2>Choose a context</h2>
                <p className="context-line">
                  Find the lab or location before recording what happened.
                </p>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <label className="search-field">
              <span className="sr-only">Choose a lab or location</span>
              <span className="search-icon" aria-hidden="true">
                <Icon name="search" />
              </span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Choose a lab or location..."
              />
            </label>
            {loading && <LoadingPanel label="Searching..." />}
            {error && <ErrorPanel message={error} />}
            <div className="picker-results">
              {results
                .filter(
                  (item) => item.type === "lab" || item.type === "location",
                )
                .map((item) => (
                  <button
                    type="button"
                    className="picker-result"
                    key={`${item.type}-${item.id}`}
                    onClick={() => void choose(item)}
                  >
                    <span>
                      <b>{item.name}</b>
                      <small>
                        {item.organization.name} ·{" "}
                        {item.type === "lab"
                          ? item.location.name
                          : item.location.locationType}
                      </small>
                    </span>
                    <span aria-hidden="true">
                      <Icon name="arrow-right" size={17} />
                    </span>
                  </button>
                ))}
            </div>
            {!loading && query.trim() && results.length === 0 && (
              <p className="muted">No matching lab or location.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** True at desktop workbench widths; below this the mobile card composition stays. */
const DESKTOP_WORKBENCH_PX = 1024;

function useDesktopWorkbench(): boolean {
  const [desktop, setDesktop] = useState(
    () =>
      typeof window === "undefined" ||
      window.innerWidth >= DESKTOP_WORKBENCH_PX,
  );
  useEffect(() => {
    // jsdom and old browsers may not implement matchMedia; the initial
    // innerWidth decision above still stands in that case.
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(`(min-width: ${DESKTOP_WORKBENCH_PX}px)`);
    const update = () => setDesktop(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return desktop;
}

function WorkbenchFilters({
  state,
  organizations,
  onChange,
}: {
  state: WorkbenchState;
  organizations: readonly Organization[];
  onChange: (changes: Partial<WorkbenchState>) => void;
}) {
  const clearing = hasActiveFilters(state);
  return (
    <div className="workbench-filters">
      <label className="workbench-filter">
        <span className="sr-only">Filter by organization</span>
        <select
          value={state.organizationId ?? ""}
          onChange={(event) =>
            onChange({
              organizationId: event.target.value || undefined,
              // A place filter from another organization would be meaningless.
              locationId: undefined,
              parentLocationId: undefined,
            })
          }
        >
          <option value="">All organizations</option>
          {organizations.map((organization) => (
            <option key={organization.id} value={organization.id}>
              {organization.name}
            </option>
          ))}
        </select>
      </label>
      <label className="workbench-filter">
        <span className="sr-only">Filter by service category</span>
        <select
          value={state.serviceCategory ?? ""}
          onChange={(event) =>
            onChange({
              serviceCategory:
                (event.target.value as WorkbenchState["serviceCategory"]) ||
                undefined,
            })
          }
        >
          <option value="">All service</option>
          <option value="pipette_calibration">Pipettes</option>
          <option value="balance_calibration">Balances</option>
        </select>
      </label>
      <label className="workbench-filter">
        <span className="sr-only">Filter by usable contact</span>
        <select
          value={state.contactPresence ?? ""}
          onChange={(event) =>
            onChange({
              contactPresence:
                (event.target.value as WorkbenchState["contactPresence"]) ||
                undefined,
            })
          }
        >
          <option value="">Any contact</option>
          <option value="known">Has usable contact</option>
          <option value="unknown">No usable contact</option>
        </select>
      </label>
      <label className="workbench-filter workbench-filter-provider">
        <span className="sr-only">Filter by provider</span>
        <input
          value={state.provider ?? ""}
          placeholder="Provider"
          onChange={(event) =>
            onChange({ provider: event.target.value.trim() || undefined })
          }
        />
      </label>
      {clearing && (
        <button
          type="button"
          className="text-button workbench-clear"
          onClick={() =>
            onChange({
              organizationId: undefined,
              locationId: undefined,
              parentLocationId: undefined,
              includeChildLocations: undefined,
              serviceCategory: undefined,
              provider: undefined,
              contactPresence: undefined,
            })
          }
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

function WorkbenchView({
  api,
  state,
  onNavigate,
  canWriteInteractions,
}: {
  api: ApiClient;
  state: WorkbenchState;
  onNavigate: (path: string) => void;
  canWriteInteractions: boolean;
}) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<readonly Organization[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localQuery, setLocalQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [drawerItem, setDrawerItem] = useState<QueueItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const { lens, grouping } = state;
  const queryKey = workbenchCacheKey(state);

  useEffect(() => {
    let active = true;
    void api
      .getOrganizations(100)
      .then((page) => {
        if (active) setOrganizations([...page.items]);
      })
      .catch(() => {
        // The organization filter is a convenience. The bounded workbench read
        // is authoritative and reports its own failures.
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    let active = true;
    const cached = workbenchCache.get(queryKey);
    setError(null);
    if (cached !== undefined) {
      // Returning from a Lab restores the working set immediately, including
      // rows already loaded with "Load more".
      setItems(cached.items);
      setNextCursor(cached.nextCursor);
      setLoading(false);
      if (!cached.stale) {
        return () => {
          active = false;
        };
      }
    } else {
      setLoading(true);
    }
    void api
      .getWorkingQueue(toWorkbenchQuery(state))
      .then((page) => {
        if (!active) return;
        setItems([...page.items]);
        setNextCursor(page.nextCursor);
        workbenchCache.set(queryKey, {
          items: [...page.items],
          nextCursor: page.nextCursor,
          scroll: workbenchCache.get(queryKey)?.scroll ?? 0,
          stale: false,
        });
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "The working set is unavailable.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // `state` is rebuilt from the URL on every render, so the stable
    // serialization is the real dependency; depending on the object would
    // refetch on every render.
  }, [api, queryKey, reloadToken]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await api.getWorkingQueue(
        toWorkbenchQuery({ ...state, cursor: nextCursor }),
      );
      const grown = [...items, ...page.items];
      setItems(grown);
      setNextCursor(page.nextCursor);
      workbenchCache.set(queryKey, {
        items: grown,
        nextCursor: page.nextCursor,
        scroll: workbenchCache.get(queryKey)?.scroll ?? 0,
        stale: false,
      });
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "More rows are unavailable.",
      );
    } finally {
      setLoadingMore(false);
    }
  }

  const visible = items.filter((item) => matchesLocalSearch(item, localQuery));
  const scopeName = placeFilterName(state, items, organizations);

  // Returning from a Lab puts the user back where they were in the list.
  useEffect(() => {
    const cached = workbenchCache.get(queryKey);
    if (cached === undefined || cached.scroll <= 0) return;
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, cached.scroll);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [queryKey]);

  function applyState(changes: Partial<WorkbenchState>) {
    onNavigate(
      `${workbenchPath()}${serializeWorkbenchState({
        ...state,
        ...changes,
        cursor: undefined,
        selectedProfileId: undefined,
      })}`,
    );
  }

  function openLab(labId: string) {
    const cached = workbenchCache.get(queryKey);
    if (cached !== undefined) {
      workbenchCache.set(queryKey, { ...cached, scroll: window.scrollY });
    }
    onNavigate(`/labs/${labId}`);
  }

  return (
    <section className="page-section workbench">
      <header className="ledger-head">
        <div className="ledger-head-title">
          <h1>{lensLabel(lens)}</h1>
          <p className="ledger-head-meta">
            {workbenchSummary(lens, items.length, scopeName)}
          </p>
        </div>
        <label className="workbench-local-search">
          <span className="sr-only">Search within this working set</span>
          <span className="search-icon" aria-hidden="true">
            <Icon name="search" size={16} />
          </span>
          <input
            value={localQuery}
            onChange={(event) => setLocalQuery(event.target.value)}
            placeholder="Search this set..."
          />
        </label>
      </header>

      <div className="workbench-toolbar">
        <div className="lens-tabs" role="tablist" aria-label="Work lens">
          {(["NOW", "COMING", "INVESTIGATE"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={lens === value}
              className={[
                "lens-tab",
                value === "NOW" ? "attention-tab" : "",
                lens === value ? "selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => applyState({ lens: value })}
            >
              {lensLabel(value)}
            </button>
          ))}
          {lens === "DISQUALIFIED_SERVICE_NEED" && (
            <button
              type="button"
              role="tab"
              aria-selected
              className="lens-tab selected"
            >
              Review service fit
            </button>
          )}
        </div>
        <WorkbenchFilters
          state={state}
          organizations={organizations}
          onChange={applyState}
        />
        <fieldset className="workbench-grouping">
          <legend className="workbench-grouping-label">Group</legend>
          {(["attention", "place"] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={
                grouping === value ? "filter-chip selected" : "filter-chip"
              }
              aria-pressed={grouping === value}
              onClick={() => applyState({ grouping: value })}
            >
              {value === "attention" ? "Attention" : "Place"}
            </button>
          ))}
        </fieldset>
      </div>

      {scopeName && (
        <p className="workbench-scope">
          Scoped to <b>{scopeName}</b>{" "}
          <button
            type="button"
            className="text-button"
            onClick={() =>
              applyState({
                organizationId: undefined,
                locationId: undefined,
                parentLocationId: undefined,
              })
            }
          >
            Work all territory
          </button>
        </p>
      )}

      {notice && (
        <div className="success-message" role="status">
          {notice}
        </div>
      )}
      {error && (
        <ErrorPanel
          message={error}
          onRetry={() => setReloadToken((value) => value + 1)}
        />
      )}
      {loading && <LoadingPanel label="Loading the working set…" />}

      {!loading && !error && visible.length === 0 && (
        <div className="empty-state">
          <strong>
            {localQuery || hasActiveFilters(state)
              ? "No rows match this working set."
              : lens === "NOW"
                ? "Nothing needs attention today."
                : lens === "COMING"
                  ? "Nothing is coming up in this horizon."
                  : "No exploration rows here."}
          </strong>
          <p>
            {localQuery || hasActiveFilters(state)
              ? "Clear a filter to widen the set. Unknown territory stays exploration, not a backlog."
              : "Browse the territory for the next useful place to work."}
          </p>
        </div>
      )}

      {!loading && !error && visible.length > 0 && (
        <>
          <WorkbenchTable
            items={visible}
            grouping={grouping}
            selectedKey={selectedKey}
            canWriteInteractions={canWriteInteractions}
            onSelect={(item) => setSelectedKey(item.serviceProfileId)}
            onOpenLab={openLab}
            onLogInteraction={(item) => {
              setNotice(null);
              setSelectedKey(item.serviceProfileId);
              setDrawerItem(item);
            }}
          />
          <div className="workbench-foot">
            <span>
              {visible.length} of {items.length} loaded rows
            </span>
            {nextCursor && (
              <button
                type="button"
                className="secondary-button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Load more rows"}
              </button>
            )}
          </div>
        </>
      )}

      {drawerItem && canWriteInteractions && (
        <WorkbenchDrawer
          api={api}
          item={drawerItem}
          onClose={() => setDrawerItem(null)}
          onSaved={(summary) => {
            setDrawerItem(null);
            setNotice(summary);
            // The row's facts are server-derived, so refresh just this set.
            workbenchCache.delete(queryKey);
            setReloadToken((value) => value + 1);
          }}
        />
      )}
    </section>
  );
}

/**
 * One quiet line under the working-set title. It states what the set is, where
 * it is scoped, and how much of it is loaded, so a salesperson always knows the
 * shape of the list in front of them without touching a control.
 */
function workbenchSummary(
  lens: WorkbenchState["lens"],
  loaded: number,
  scope: string | null,
): string {
  const noun = lens === "INVESTIGATE" ? "profiles" : "Labs";
  const count = loaded === 1 ? `1 ${noun}` : `${loaded} ${noun}`;
  const scopeText = scope ? ` in ${scope}` : "";
  return `${count}${scopeText} · ordered by next attention`;
}

function placeFilterName(
  state: WorkbenchState,
  items: readonly QueueItem[],
  organizations: readonly Organization[],
): string | null {
  if (state.locationId) {
    const match = items.find((item) => item.location.id === state.locationId);
    return match?.location.name ?? "this place";
  }
  if (state.organizationId) {
    return (
      organizations.find(
        (organization) => organization.id === state.organizationId,
      )?.name ?? "this organization"
    );
  }
  return null;
}

/**
 * The workbench read carries the actionable relationship summary, not a full
 * Contact record. The capture form needs identity fields to preselect the
 * person; the server stays the authority on the relationship itself.
 */
function contactFromQueue(item: QueueItem): Contact {
  const contact = item.contact;
  if (contact === null) {
    throw new Error("Row has no usable contact.");
  }
  return {
    id: contact.id,
    organizationId: item.organization.id,
    name: contact.name,
    title: contact.title,
    department: "",
    email: contact.email,
    phone: contact.phone,
    contactSource: "field_discovery",
    verificationStatus: contact.verificationStatus,
    isActive: true,
  };
}

/** The phone/tablet lens list keeps the existing card composition. */
function MobileQueueView({
  api,
  state,
  onNavigate,
}: {
  api: ApiClient;
  state: WorkbenchState;
  onNavigate: (path: string) => void;
}) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lens = state.lens;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .getWorkingQueue(toWorkbenchQuery({ ...state, lens }))
      .then((page) => {
        if (active) setItems([...page.items]);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "The working queue is unavailable.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // The lens is serialized into the URL, so it is the stable dependency.
  }, [api, state.lens, lens]);

  return (
    <section className="page-section">
      <div className="page-intro">
        <div>
          <p className="eyebrow">Today</p>
          <h1>{lensLabel(lens)}</h1>
        </div>
      </div>
      <div className="lens-tabs" role="tablist" aria-label="Work lens">
        {(["NOW", "COMING", "INVESTIGATE"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={lens === value}
            className={[
              "lens-tab",
              value === "NOW" ? "attention-tab" : "",
              lens === value ? "selected" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() =>
              onNavigate(
                `${workbenchPath()}${serializeWorkbenchState({ ...state, lens: value })}`,
              )
            }
          >
            {lensLabel(value)}
          </button>
        ))}
      </div>
      {loading && (
        <LoadingPanel label="Calculating from the current CRM facts…" />
      )}
      {error && <ErrorPanel message={error} />}
      {!loading && !error && items.length === 0 && (
        <div className="empty-state">
          <strong>
            {lens === "NOW"
              ? "Nothing needs attention today."
              : lens === "COMING"
                ? "Nothing planned here."
                : "No exploration rows here."}
          </strong>
          <p>
            Unknown or incomplete territory remains available without becoming a
            forced task.
          </p>
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="queue-list">
          {items.map((item) => (
            <QueueCard
              key={item.serviceProfileId}
              item={item}
              onOpen={() => onNavigate(`/labs/${item.lab.id}`)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function TodayView({
  api,
  state,
  onNavigate,
  canWriteInteractions,
}: {
  api: ApiClient;
  state: WorkbenchState;
  onNavigate: (path: string) => void;
  canWriteInteractions: boolean;
}) {
  const desktop = useDesktopWorkbench();
  if (!desktop) {
    return <MobileQueueView api={api} state={state} onNavigate={onNavigate} />;
  }
  return (
    <WorkbenchView
      api={api}
      state={state}
      onNavigate={onNavigate}
      canWriteInteractions={canWriteInteractions}
    />
  );
}

/**
 * The desktop workbench logs interactions without leaving the working set. This
 * wraps the existing UI-2B capture rather than restating it.
 */
function WorkbenchDrawer({
  api,
  item,
  onClose,
  onSaved,
}: {
  api: ApiClient;
  item: QueueItem;
  onClose: () => void;
  onSaved: (summary: string) => void;
}) {
  return (
    <div className="workbench-drawer">
      <p className="workbench-drawer-banner" role="note">
        Logging from your working set. Filters, grouping, and your place in the
        list are kept.
      </p>
      <QuickInteractionCapture
        api={api}
        labId={item.lab.id}
        labName={item.lab.name}
        location={item.location}
        contacts={item.contact ? [contactFromQueue(item)] : []}
        initialContactId={item.contact?.id}
        presentation="drawer"
        onClose={onClose}
        onSaved={async (_result, summary) => onSaved(summary)}
      />
    </div>
  );
}

/**
 * The pocket field notebook entry. Mobile is not scaled-down desktop: it is one
 * compact ruled entry per Lab carrying the facts as prose, the person, and the
 * next attention annotation — not a stack of labelled desktop cells.
 */
function QueueCard({ item, onOpen }: { item: QueueItem; onOpen: () => void }) {
  const missing = missingFacts(item);
  return (
    <button
      type="button"
      className="ledger-entry"
      data-attention={needsAttentionMark(item) || undefined}
      onClick={onOpen}
    >
      <span className="entry-head">
        <span className="entry-name">{item.lab.name}</span>
        <span className="entry-room">{item.lab.roomOrSuite || "—"}</span>
      </span>
      <span className="entry-place">
        {item.organization.name} · {item.location.name}
      </span>
      <span className="entry-facts">{serviceFacts(item)}</span>
      {item.contact ? (
        <span className="entry-contact">
          {item.contact.name}
          {item.contact.verificationStatus === "unverified" && (
            <span className="entry-quiet"> · unverified</span>
          )}
        </span>
      ) : (
        <span className="entry-quiet">No usable contact yet</span>
      )}
      <span className="entry-attention">
        <span className="entry-next">
          Next attention {formatDate(item.queue.nextMeaningfulSalesDate)}
        </span>
        <span className="entry-reason">{queueReasonShort(item.queue)}</span>
      </span>
      {missing && <span className="entry-missing">{missing}</span>}
    </button>
  );
}

/**
 * Search result kinds in plain words. The server's `type` is a canonical
 * discriminator, not display copy.
 */
function resultTypeLabel(type: SearchItem["type"]): string {
  if (type === "lab") return "Lab";
  if (type === "location") return "Place";
  if (type === "organization") return "Organization";
  if (type === "organizational_unit") return "Organizational Unit";
  return "Person";
}

function SearchView({
  api,
  onNavigate,
  initialQuery,
}: {
  api: ApiClient;
  onNavigate: (path: string) => void;
  initialQuery?: string | undefined;
}) {
  const [query, setQuery] = useState(initialQuery ?? "");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      void api
        .search(query.trim())
        .then((result) => {
          if (active) setResults(result.items);
        })
        .catch((caught: unknown) => {
          if (active)
            setError(
              caught instanceof Error
                ? caught.message
                : "Search is unavailable.",
            );
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 275);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, query]);

  function open(item: SearchItem) {
    if (item.type === "lab") onNavigate(`/labs/${item.lab.id}`);
    else if (item.type === "location")
      onNavigate(`/locations/${item.location.id}`);
    else onNavigate(`/organizations/${item.organization.id}`);
  }

  return (
    <section className="page-section">
      <div className="page-intro">
        <div>
          <p className="eyebrow">Territory search</p>
          <h1>Find the context you remember.</h1>
          <p className="lede">
            Search the server’s shared CRM knowledge; imported reference people
            stay visibly unverified.
          </p>
        </div>
      </div>
      <label className="search-field">
        <span className="sr-only">
          Search labs, people, locations, and organizations
        </span>
        <span className="search-icon" aria-hidden="true">
          <Icon name="search" />
        </span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search this territory..."
        />
      </label>
      {loading && <LoadingPanel label="Searching…" />}
      {error && <ErrorPanel message={error} />}
      {!loading && !error && query && results.length === 0 && (
        <div className="empty-state">
          <strong>No matching CRM context.</strong>
          <p>
            Try a partial lab name, person, building, department, email, or
            provider.
          </p>
        </div>
      )}
      <div className="search-results">
        {results.map((item) => {
          const resultContent = (
            <>
              <div>
                <span className="result-type">
                  {resultTypeLabel(item.type)}
                </span>
                <h3>{item.name}</h3>
                <p>
                  Matched on {matchFieldLabel(item.matchingField).toLowerCase()}
                </p>
              </div>
              <div className="result-context">
                <b>{item.organization.name}</b>
                {item.type === "lab" && (
                  <span>
                    {item.location.name} ·{" "}
                    {item.lab.roomOrSuite || "Room not recorded"}
                  </span>
                )}
                {item.type === "location" && (
                  <span>{item.location.locationType}</span>
                )}
                {item.type === "contact" && (
                  <span>
                    {item.contact.title || "Contact"} ·{" "}
                    {verificationLabel(item.contact.verificationStatus)}
                  </span>
                )}
                {item.type === "organizational_unit" && (
                  <span>
                    {item.organizationalUnit.unitType.replaceAll("_", " ")}
                  </span>
                )}
              </div>
            </>
          );
          return (
            <button
              type="button"
              className="search-result"
              key={`${item.type}-${item.id}`}
              onClick={() => open(item)}
            >
              {resultContent}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function LocationView({
  api,
  locationId,
  onNavigate,
  onBack,
  canWriteTerritory,
  canWriteInteractions,
}: {
  api: ApiClient;
  locationId: string;
  onNavigate: (path: string) => void;
  onBack: () => void;
  canWriteTerritory: boolean;
  canWriteInteractions: boolean;
}) {
  const [detail, setDetail] = useState<LocationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [newLabOpen, setNewLabOpen] = useState(false);
  const [createdLab, setCreatedLab] = useState<Lab | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    void api
      .getLocation(locationId)
      .then((value) => {
        if (active) setDetail(value);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "The location is unavailable.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, locationId, refresh]);
  if (loading) return <LoadingPanel label="Loading location context…" />;
  if (error || !detail)
    return <ErrorPanel message={error ?? "Location not found."} />;
  const profilesAtLocation = detail.labs.items.flatMap((entry) =>
    entry.serviceProfiles.map((profile) => ({ profile, lab: entry.lab })),
  );
  const attentionProfiles = profilesAtLocation.filter(
    ({ profile }) =>
      profile.queue.queueCode === "NOW" ||
      profile.queue.queueCode === "DISQUALIFIED_SERVICE_NEED",
  );
  const comingProfiles = profilesAtLocation.filter(
    ({ profile }) => profile.queue.queueCode === "COMING",
  );
  return (
    <section className="page-section">
      <BackLink onClick={onBack} label="Back to territory" />
      <div className="detail-header">
        <div>
          <p className="eyebrow">Location · {detail.location.locationType}</p>
          <h1>{detail.location.name}</h1>
          <p className="breadcrumb">
            {detail.organization.name}
            {detail.parentLocationBreadcrumb.length > 0 ? " · " : ""}
            {detail.parentLocationBreadcrumb
              .map((item) => item.name)
              .join(" / ")}
          </p>
        </div>
        <div className="page-actions">
          {canWriteInteractions && (
            <button
              type="button"
              className="primary-button"
              onClick={() => setCaptureOpen(true)}
            >
              <Icon name="plus" size={16} /> Record interaction
            </button>
          )}
          <button
            type="button"
            className="secondary-button"
            onClick={() =>
              onNavigate(
                `${workbenchPath()}${serializeWorkbenchState({
                  lens: "NOW",
                  grouping: "place",
                  organizationId: detail.organization.id,
                  // A building is worked together with the rooms inside it.
                  locationId: detail.location.id,
                  includeChildLocations: true,
                })}`,
              )
            }
          >
            Work this place
          </button>
          {canWriteTerritory && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setNewLabOpen(true)}
            >
              <Icon name="plus" size={16} /> New lab
            </button>
          )}
        </div>
      </div>
      {notice && (
        <div className="success-message" role="status">
          {notice}
        </div>
      )}
      {(attentionProfiles.length > 0 || comingProfiles.length > 0) && (
        <div className="location-signals">
          {attentionProfiles.length > 0 && (
            <section className="signal-block attention-signal">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Needs attention here</p>
                  <h2>{attentionProfiles.length} worth a look now</h2>
                </div>
              </div>
              <div className="signal-list">
                {attentionProfiles.map(({ profile, lab }) => (
                  <LocationSignalRow
                    key={profile.serviceProfileId}
                    profile={profile}
                    lab={lab}
                    onOpen={() => onNavigate(`/labs/${lab.id}`)}
                  />
                ))}
              </div>
            </section>
          )}
          {comingProfiles.length > 0 && (
            <section className="signal-block coming-signal">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Coming up</p>
                  <h2>{comingProfiles.length} approaching</h2>
                </div>
              </div>
              <div className="signal-list">
                {comingProfiles.map(({ profile, lab }) => (
                  <LocationSignalRow
                    key={profile.serviceProfileId}
                    profile={profile}
                    lab={lab}
                    onOpen={() => onNavigate(`/labs/${lab.id}`)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
      <div className="detail-grid">
        <div className="detail-main">
          <section className="content-card">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Known territory</p>
                <h2>Labs here</h2>
              </div>
              <button
                type="button"
                className="text-button"
                onClick={() => onNavigate(`/search`)}
              >
                Search here
              </button>
            </div>
            {detail.labs.items.length === 0 ? (
              <p className="muted">No labs recorded at this location yet.</p>
            ) : (
              <div className="lab-list">
                {detail.labs.items.map((entry) => (
                  <button
                    type="button"
                    className="lab-row"
                    key={entry.lab.id}
                    onClick={() => onNavigate(`/labs/${entry.lab.id}`)}
                  >
                    <div>
                      <b>{entry.lab.name}</b>
                      <span>
                        {entry.lab.roomOrSuite || "Room not recorded"}
                      </span>
                    </div>
                    <div className="profile-chips">
                      {entry.serviceProfiles.length === 0 ? (
                        <span className="subtle-chip">
                          No service need recorded
                        </span>
                      ) : (
                        entry.serviceProfiles.map((profile) => (
                          <span
                            className={`queue-badge mini queue-${profile.queue.queueCode.toLowerCase()}`}
                            key={profile.serviceProfileId}
                          >
                            {categoryLabel(profile.serviceCategory)} ·{" "}
                            {queueLabel(profile.queue.queueCode)}
                          </span>
                        ))
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
          <section className="content-card">
            <div className="section-heading">
              <div>
                <p className="eyebrow">People / local intelligence</p>
                <h2>People here</h2>
              </div>
            </div>
            {detail.contacts.length === 0 ? (
              <p className="muted">No contacts recorded for this location.</p>
            ) : (
              <div className="contact-list">
                {detail.contacts.map((relationship) => (
                  <ContactRow
                    contact={relationship.contact}
                    context={relationship.labName}
                    key={`${relationship.labId}:${relationship.contact.id}`}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
        <aside className="detail-side">
          <section className="content-card">
            <p className="eyebrow">Recent information</p>
            {detail.recentInteractions.items.length === 0 ? (
              <p className="muted">No recent interactions.</p>
            ) : (
              <div className="history-list">
                {detail.recentInteractions.items
                  .slice(0, 8)
                  .map((interaction) => (
                    <HistoryRow
                      key={interaction.id}
                      interaction={interaction}
                      labName={
                        detail.labs.items.find(
                          (entry) => entry.lab.id === interaction.labId,
                        )?.lab.name
                      }
                    />
                  ))}
              </div>
            )}
          </section>
          <section className="content-card">
            <p className="eyebrow">Access context</p>
            <p>
              {detail.location.accessNotes ||
                "No reusable access note recorded."}
            </p>
          </section>
        </aside>
      </div>
      {captureOpen && canWriteInteractions && (
        <QuickInteractionCapture
          api={api}
          location={detail.location}
          contacts={detail.contacts.map((relationship) => relationship.contact)}
          availableLabs={detail.labs.items.map((entry) => entry.lab)}
          onRequestNewLab={() => {
            setCaptureOpen(false);
            setNewLabOpen(true);
          }}
          onClose={() => setCaptureOpen(false)}
          onSaved={async (_result, summary) => {
            setCaptureOpen(false);
            setNotice(summary);
            setRefresh((value) => value + 1);
          }}
        />
      )}
      {newLabOpen && canWriteTerritory && (
        <NewLabForm
          api={api}
          location={detail.location}
          onClose={() => setNewLabOpen(false)}
          onCreated={(newLab) => {
            setNewLabOpen(false);
            setCreatedLab(newLab);
            setRefresh((value) => value + 1);
          }}
          onUseExisting={(labId) => {
            setNewLabOpen(false);
            onNavigate(`/labs/${labId}`);
          }}
        />
      )}
      {createdLab && canWriteInteractions && (
        <QuickInteractionCapture
          api={api}
          labId={createdLab.id}
          labName={createdLab.name}
          location={detail.location}
          contacts={[]}
          onClose={() => setCreatedLab(null)}
          onSaved={async (_result, summary) => {
            setCreatedLab(null);
            setNotice(summary);
            setRefresh((value) => value + 1);
          }}
        />
      )}
    </section>
  );
}

function LocationSignalRow({
  profile,
  lab,
  onOpen,
}: {
  profile: ServiceProfileSummary;
  lab: Lab;
  onOpen: () => void;
}) {
  return (
    <button type="button" className="signal-row" onClick={onOpen}>
      <span className="signal-copy">
        <b>{lab.name}</b>
        <small>
          {[lab.roomOrSuite, categoryLabel(profile.serviceCategory)]
            .filter(Boolean)
            .join(" · ")}
          {formatObservedTiming(profile.projection) !== "Timing unknown"
            ? ` · ${formatObservedTiming(profile.projection)}`
            : ""}
        </small>
      </span>
      <span className="signal-reason">
        {profile.queue.queueCode === "NOW" ||
        profile.queue.queueCode === "DISQUALIFIED_SERVICE_NEED"
          ? queueReason(profile.queue)
          : `Next attention ${formatDate(profile.queue.nextMeaningfulSalesDate)}`}
        <Icon name="arrow-right" size={16} />
      </span>
    </button>
  );
}

function LabView({
  api,
  labId,
  onBack,
  onOpenTerritory,
  fromWorkbench,
  canWriteInteractions,
  canCorrectObservations,
}: {
  api: ApiClient;
  labId: string;
  onBack: () => void;
  onOpenTerritory: () => void;
  fromWorkbench: boolean;
  canWriteInteractions: boolean;
  canCorrectObservations: boolean;
}) {
  const [detail, setDetail] = useState<LabDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [correctionObservationId, setCorrectionObservationId] = useState<
    string | null
  >(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    void api
      .getLab(labId)
      .then((value) => {
        if (active) setDetail(value);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "The lab is unavailable.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, labId, refresh]);
  if (loading) return <LoadingPanel label="Loading lab understanding…" />;
  if (error || !detail)
    return <ErrorPanel message={error ?? "Lab not found."} />;
  const contacts = detail.contacts.map((relationship) => relationship.contact);
  return (
    <section className="page-section">
      <BackLink
        onClick={onBack}
        label={fromWorkbench ? "Back to working set" : "Back to territory"}
      />
      {fromWorkbench && (
        <button
          type="button"
          className="text-button lab-territory-link"
          onClick={onOpenTerritory}
        >
          View this Lab in territory
        </button>
      )}
      <div className="detail-header">
        <div>
          <p className="eyebrow">Lab</p>
          <h1>{detail.lab.name}</h1>
          <p className="breadcrumb">
            {[
              detail.organization.name,
              detail.primaryLocation.name,
              detail.lab.roomOrSuite,
            ]
              .filter(Boolean)
              .join(" · ") || "Lab context"}
          </p>
        </div>
        {canWriteInteractions && (
          <button
            type="button"
            className="primary-button"
            onClick={() => setCaptureOpen(true)}
          >
            <Icon name="plus" size={16} /> Record interaction
          </button>
        )}
      </div>
      {notice && (
        <div className="success-message" role="status">
          {notice}
        </div>
      )}
      <div className="profile-grid">
        {detail.serviceProfiles.length === 0 ? (
          <section className="empty-state full-span">
            <strong>No pipette or balance service need recorded yet.</strong>
            <p>Save the next useful fact without inventing a category.</p>
          </section>
        ) : (
          detail.serviceProfiles.map((profile) => (
            <ProfileCard
              key={profile.serviceProfileId}
              profile={profile}
              canCorrect={canCorrectObservations}
              onCorrect={(observationId) =>
                setCorrectionObservationId(observationId)
              }
            />
          ))
        )}
      </div>
      <div className="detail-grid lab-detail-grid">
        <div className="detail-main">
          <section className="content-card">
            <div className="section-heading">
              <div>
                <p className="eyebrow">People</p>
                <h2>Lab contacts</h2>
              </div>
            </div>
            {detail.contacts.length === 0 ? (
              <p className="muted">No lab relationship recorded yet.</p>
            ) : (
              <div className="contact-list">
                {detail.contacts
                  .filter(
                    (relationship) =>
                      relationship.relationshipStatus !== "former",
                  )
                  .map((relationship) => (
                    <RelationshipRow
                      relationship={relationship}
                      key={relationship.contact.id}
                    />
                  ))}
              </div>
            )}
          </section>
          <section className="content-card">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Chronological memory</p>
                <h2>Recent interactions</h2>
              </div>
              {!historyExpanded && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => setHistoryExpanded(true)}
                >
                  Load more history
                </button>
              )}
            </div>
            <div className="history-list">
              {detail.recentInteractions.items.map((interaction) => (
                <HistoryRow
                  key={interaction.id}
                  interaction={interaction}
                  labName={detail.lab.name}
                />
              ))}
            </div>
            {historyExpanded && (
              <ExpandedHistory
                api={api}
                labId={labId}
                labName={detail.lab.name}
              />
            )}
          </section>
        </div>
        <aside className="detail-side">
          <section className="content-card">
            <p className="eyebrow">Lab note</p>
            <p>{detail.lab.notes || "No lab note recorded."}</p>
          </section>
        </aside>
      </div>
      {captureOpen && canWriteInteractions && (
        <QuickInteractionCapture
          api={api}
          labId={detail.lab.id}
          labName={detail.lab.name}
          location={detail.primaryLocation}
          contacts={contacts}
          relationships={detail.contacts}
          onClose={() => setCaptureOpen(false)}
          onSaved={async (_result, summary) => {
            setCaptureOpen(false);
            setNotice(summary);
            setRefresh((value) => value + 1);
          }}
        />
      )}
      {correctionObservationId && canCorrectObservations && (
        <ObservationCorrectionForm
          api={api}
          observationId={correctionObservationId}
          onClose={() => setCorrectionObservationId(null)}
          onSaved={async () => {
            setCorrectionObservationId(null);
            setNotice(
              "Corrected recorded information; Lab understanding refreshed.",
            );
            setRefresh((value) => value + 1);
          }}
          onStale={async () => {
            setCorrectionObservationId(null);
            setNotice(
              "Another user corrected that fact; Lab understanding refreshed.",
            );
            setRefresh((value) => value + 1);
          }}
        />
      )}
    </section>
  );
}

/**
 * One recorded fact in a Lab profile. Human-readable labels stay here; the
 * canonical observation types and values come from the server projection.
 */
function Fact({
  label,
  value,
  action,
}: {
  label: string;
  value: string;
  action?: { name: string; onClick: () => void } | undefined;
}) {
  return (
    <div className="profile-fact">
      <span className="label">{label}</span>
      <b>{value}</b>
      {action && (
        <button
          type="button"
          className="text-button fact-action"
          aria-label={`Correct ${action.name}`}
          onClick={action.onClick}
        >
          Correct
        </button>
      )}
    </div>
  );
}

function ProfileCard({
  profile,
  canCorrect,
  onCorrect,
}: {
  profile: ServiceProfileSummary;
  canCorrect: boolean;
  onCorrect: (observationId: string) => void;
}) {
  const derivedWindow = formatDerivedWindow(profile.projection);
  const currentQuantityObservationId =
    profile.projection.currentQuantityObservationId;
  const timingObservationId = profile.projection.timingObservationId;
  const providerObservationId = profile.projection.providerObservationId;
  const accreditationObservationId =
    profile.projection.accreditationRequirementObservationId;
  return (
    <section className="profile-card">
      <div className="profile-card-heading">
        <div>
          <p className="eyebrow">Current understanding</p>
          <h2>{categoryLabel(profile.serviceCategory)}</h2>
        </div>
        <span
          className={`queue-badge queue-${profile.queue.queueCode.toLowerCase()}`}
        >
          {queueLabel(profile.queue.queueCode)}
        </span>
      </div>
      <div className="profile-facts">
        <Fact
          label="Quantity"
          value={formatQuantity(profile.projection)}
          action={
            canCorrect && currentQuantityObservationId
              ? {
                  name: "quantity",
                  onClick: () => onCorrect(currentQuantityObservationId),
                }
              : undefined
          }
        />
        <Fact
          label="Normal timing"
          value={formatObservedTiming(profile.projection)}
          action={
            canCorrect && timingObservationId
              ? {
                  name: "timing",
                  onClick: () => onCorrect(timingObservationId),
                }
              : undefined
          }
        />
        <Fact
          label="Provider"
          value={formatProvider(profile.projection)}
          action={
            canCorrect && providerObservationId
              ? {
                  name: "provider",
                  onClick: () => onCorrect(providerObservationId),
                }
              : undefined
          }
        />
        <Fact
          label="Accreditation"
          value={profile.projection.accreditationRequirement}
          action={
            canCorrect && accreditationObservationId
              ? {
                  name: "accreditation",
                  onClick: () => onCorrect(accreditationObservationId),
                }
              : undefined
          }
        />
      </div>
      <div className="profile-attention">
        <div>
          <span className="label">Next attention</span>
          <b>{formatDate(profile.queue.nextMeaningfulSalesDate)}</b>
        </div>
        {derivedWindow && (
          <div>
            <span className="label">Expected window</span>
            <b>{derivedWindow}</b>
          </div>
        )}
        <p>{queueReason(profile.queue)}</p>
      </div>
      {profile.queue.knowledgeGaps.length > 0 && (
        <div className="gap-block">
          <span className="label">Still needed</span>
          <div className="gap-list">
            {profile.queue.knowledgeGaps.map((gap) => (
              <span className="gap-chip" key={gap}>
                {gapLabel(gap)}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ExpandedHistory({
  api,
  labId,
  labName,
}: {
  api: ApiClient;
  labId: string;
  labName: string;
}) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<LabDetail["recentInteractions"]["items"]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api
      .getLabInteractions(labId)
      .then((result) => setItems(result.items))
      .catch((caught: unknown) =>
        setError(
          caught instanceof Error ? caught.message : "History is unavailable.",
        ),
      )
      .finally(() => setLoading(false));
  }, [api, labId]);
  if (loading) return <p className="muted">Loading full history…</p>;
  if (error) return <p className="error-message">{error}</p>;
  return (
    <div className="history-list history-expanded">
      {items.map((interaction) => (
        <HistoryRow
          key={interaction.id}
          interaction={interaction}
          labName={labName}
        />
      ))}
    </div>
  );
}

function ContactRow({
  contact,
  context,
}: {
  contact: Contact;
  context?: string;
}) {
  return (
    <div className="contact-row">
      <div>
        <b>{contact.name}</b>
        <span>
          {[contact.title, contact.department].filter(Boolean).join(" · ") ||
            "Contact"}
        </span>
        {context && <small>{context}</small>}
      </div>
      <div className="contact-status">
        <span
          className={
            contact.verificationStatus === "verified"
              ? "verified"
              : "unverified"
          }
        >
          {verificationLabel(contact.verificationStatus)}
        </span>
        <small>{contact.contactSource.replaceAll("_", " ")}</small>
      </div>
    </div>
  );
}

function RelationshipRow({
  relationship,
}: {
  relationship: LabDetail["contacts"][number];
}) {
  return (
    <div className="contact-row">
      <div>
        <b>{relationship.contact.name}</b>
        <span>
          {relationship.relationshipRole?.replaceAll("_", " ") ??
            "Role not asserted"}{" "}
          · {relationship.influenceScope ?? "Scope not asserted"}
        </span>
        {relationship.scopeNote && <small>{relationship.scopeNote}</small>}
      </div>
      <div className="contact-status">
        <span
          className={
            relationship.contact.verificationStatus === "verified"
              ? "verified"
              : "unverified"
          }
        >
          {verificationLabel(relationship.contact.verificationStatus)}
        </span>
        <small>{relationship.relationshipStatus}</small>
      </div>
    </div>
  );
}

function HistoryRow({
  interaction,
  labName,
}: {
  interaction: LabDetail["recentInteractions"]["items"][number];
  labName?: string | undefined;
}) {
  const summary = historySummary(interaction, labName);
  return (
    <article
      className="history-row"
      data-correction={summary.isCorrection || undefined}
    >
      <div className="history-date">
        {formatLocalDate(interaction.occurredAt)}
      </div>
      <div>
        <b>{summary.headline}</b>
        {summary.detail && <span>{summary.detail}</span>}
        {interaction.note && !summary.isCorrection && <p>{interaction.note}</p>}
        <details className="history-details">
          <summary>Details</summary>
          <span>
            {interaction.interactionType.replaceAll("_", " ")} ·{" "}
            {interaction.captureOutcome.replaceAll("_", " ")}
          </span>
          {interaction.observations.length > 0 && (
            <div className="observation-tags">
              {interaction.observations.map((observation) => {
                const isOriginal = Boolean(
                  observation.supersededByObservationId,
                );
                const isCorrection = Boolean(
                  observation.supersedesObservationId,
                );
                return (
                  <span key={observation.id}>
                    {formatHistoryObservation(observation)}
                    {isOriginal && " · Corrected later"}
                    {isCorrection && " · Correction to previous record"}
                  </span>
                );
              })}
            </div>
          )}
        </details>
      </div>
    </article>
  );
}

function historySummary(
  interaction: LabDetail["recentInteractions"]["items"][number],
  labName?: string,
): { headline: string; detail?: string; isCorrection: boolean } {
  const correctionNote = interaction.note.match(/^Correction:\s*(.+)$/iu)?.[1];
  if (correctionNote) {
    const text = /^(corrected|updated|changed)\b/iu.test(correctionNote)
      ? correctionNote
      : `Corrected information: ${correctionNote}`;
    return {
      headline: text,
      detail: "This update replaced an earlier memory.",
      isCorrection: true,
    };
  }

  const quantities = interaction.observations.filter(
    (observation) => observation.observationType === "quantity",
  );
  const timings = interaction.observations.filter(
    (observation) => observation.observationType === "timing",
  );
  const providers = interaction.observations.filter(
    (observation) => observation.observationType === "provider",
  );
  const responsibilities = interaction.observations.filter(
    (observation) => observation.observationType === "contact_responsibility",
  );
  const firstServiceCategory =
    quantities[0]?.serviceCategory ??
    timings[0]?.serviceCategory ??
    providers[0]?.serviceCategory;
  const equipmentLabel = firstServiceCategory
    ? firstServiceCategory === "pipette_calibration"
      ? "pipettes"
      : "balances"
    : "equipment";
  const quantity = quantities[0]?.payload;
  const timing = timings[0]?.payload;
  const provider = providers[0]?.payload;
  const quantityText =
    quantity && "value" in quantity
      ? `${quantity.qualifier === "approximate" ? "~" : ""}${String(quantity.value)}`
      : null;
  const timingText = timing
    ? formatHistoryObservation({ observationType: "timing", payload: timing })
    : null;
  const providerText =
    provider && "name" in provider ? String(provider.name) : null;
  let headline: string;
  if (quantityText && providerText) {
    headline = `Confirmed ${quantityText} ${equipmentLabel} with ${providerText}.`;
  } else if (quantityText) {
    headline = `Confirmed ${quantityText} ${equipmentLabel}.`;
  } else if (timingText && firstServiceCategory) {
    headline = `${firstServiceCategory === "pipette_calibration" ? "Pipette" : "Balance"} timing is ${timingText}.`;
  } else if (providerText && firstServiceCategory) {
    headline = `${firstServiceCategory === "pipette_calibration" ? "Pipette" : "Balance"} provider is ${providerText}.`;
  } else {
    const subject =
      labName ?? (interaction.labId ? "the lab" : "this location");
    headline =
      interaction.captureOutcome === "nobody_available"
        ? `Nobody available at ${subject}.`
        : interaction.captureOutcome === "wrong_person"
          ? `Reached the wrong person at ${subject}.`
          : interaction.captureOutcome === "no_new_information" ||
              interaction.captureOutcome === "no_answer" ||
              interaction.captureOutcome === "no_response"
            ? `No useful information from ${subject}.`
            : interaction.captureOutcome === "referred_elsewhere"
              ? "Referred to another contact."
              : `${interaction.primaryContact?.name ?? "Someone"} shared useful information.`;
  }
  const responsibility = responsibilities[0];
  const detail = responsibility
    ? `${interaction.primaryContact?.name ?? "This contact"}: ${
        responsibility.payload.role == null
          ? "Lab relationship recorded; role not asserted"
          : `${String(responsibility.payload.role).replaceAll("_", " ")} role recorded`
      }.`
    : timingText && quantityText
      ? `Timing: ${timingText}.`
      : undefined;
  return { headline, ...(detail ? { detail } : {}), isCorrection: false };
}

function formatHistoryObservation(observation: {
  observationType: string;
  payload: Record<string, unknown>;
}): string {
  const payload = observation.payload;
  if (observation.observationType === "quantity" && "value" in payload) {
    return `${payload.qualifier === "approximate" ? "~" : ""}${String(payload.value)} ${payload.scope === "historical_service" ? "historical service" : "instruments"}`;
  }
  if (observation.observationType === "timing") {
    if (payload.precision === "month") {
      const month = Number(payload.month);
      return `${new Intl.DateTimeFormat("en", {
        month: "long",
        timeZone: "UTC",
      }).format(
        new Date(Date.UTC(2020, month - 1, 1)),
      )}${payload.year ? ` ${payload.year}` : ""}`;
    }
    if (payload.precision === "date_range") {
      return `${String(payload.rangeStart)} – ${String(payload.rangeEnd)}`;
    }
    if (payload.precision === "season") {
      return `${String(payload.season)}${payload.year ? ` ${payload.year}` : ""}`;
    }
    if (payload.precision === "exact_date") return String(payload.exactDate);
    if (payload.precision === "relative") return String(payload.relativeState);
    return "Timing unknown";
  }
  if (observation.observationType === "provider" && "name" in payload) {
    return String(payload.name);
  }
  if (
    observation.observationType === "accreditation_requirement" &&
    "requirement" in payload
  ) {
    return `Accreditation: ${String(payload.requirement)}`;
  }
  return observation.observationType.replaceAll("_", " ");
}

function BackLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" className="back-link" onClick={onClick}>
      <Icon name="back" size={16} /> {label}
    </button>
  );
}
